import React, { useEffect, useState } from "react";
import { api } from "@/lib/kielo";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Sparkles, ArrowRight, LogIn, UserCheck, GraduationCap, BookOpen, Plane, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1538332576228-eb5b4c4de6f5?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

const HELSINKI = [
  { src: "https://images.unsplash.com/photo-1557261651-a6beab93541f?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", alt: "Helsinki tram on a city street" },
  { src: "https://images.unsplash.com/photo-1568008842610-6642e5afe517?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", alt: "Aerial view of Helsinki by the sea" },
  { src: "https://images.unsplash.com/photo-1538332576228-eb5b4c4de6f5?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", alt: "Helsinki Cathedral, the white church" },
];

export default function Home() {
  const { user, login } = useApp();
  const { t, lang } = useI18n();
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    api.get("/curriculum", { params: { lang } }).then(({ data }) => setLessons(data));
  }, [lang]);

  const a1 = lessons.filter((l) => l.level === "A1");
  const a2 = lessons.filter((l) => l.level === "A2");
  const b1 = lessons.filter((l) => l.level === "B1");

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Finnish lake and pine forest" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E382B]/95 via-[#0E382B]/82 to-[#0E382B]/45" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 lg:py-32">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-[#FDEEE5] text-sm font-medium bg-white/10 border border-white/20 rounded-full px-3.5 py-1.5 backdrop-blur-sm">
              <Sparkles className="w-4 h-4" /> {t("home.badge")}
            </span>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.08] tracking-tight">
              {t("home.title")}
            </h1>
            <p className="mt-5 text-lg text-[#E8F0EC]/90 leading-relaxed max-w-xl">{t("home.sub")}</p>
            <p className="mt-4 inline-flex items-center gap-2 text-[#FDEEE5] font-medium bg-[#E46A28]/90 rounded-full px-4 py-2 text-sm" data-testid="hero-coming-badge">
              <Plane className="w-4 h-4" /> {t("home.comingBadge")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={login} className="rounded-full bg-[#E46A28] hover:bg-[#c9591f] text-white px-6 h-12 text-base" data-testid="hero-enrol-button">
                <LogIn className="w-4 h-4 mr-1.5" /> {t("home.ctaEnrol")}
              </Button>
              <a href="#syllabus">
                <Button variant="outline" className="rounded-full bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white px-6 h-12 text-base" data-testid="hero-learn-more">
                  {t("home.ctaLearnMore")}
                </Button>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-8 text-[#E8F0EC]/85">
              <Stat n={lessons.length ? String(lessons.length) : "28"} label={t("home.statLessons")} />
              <Stat n="50+" label={t("home.statWords")} />
              <Stat n="15" label={t("home.statCases")} />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0E382B] tracking-tight text-center">{t("home.howTitle")}</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: LogIn, tt: "home.how1Title", bb: "home.how1Body", c: "#E8F0EC", ic: "#0E5A35" },
            { icon: UserCheck, tt: "home.how2Title", bb: "home.how2Body", c: "#FEF3C7", ic: "#B45309" },
            { icon: GraduationCap, tt: "home.how3Title", bb: "home.how3Body", c: "#EDF2FE", ic: "#1E429F" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl bg-white border border-[#E2DFD7] p-7">
              <span className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.c }}>
                <s.icon className="w-6 h-6" style={{ color: s.ic }} />
              </span>
              <h3 className="mt-4 font-serif text-xl font-semibold text-[#16201B]">{t(s.tt)}</h3>
              <p className="mt-1.5 text-[#52635B] leading-relaxed">{t(s.bb)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HELSINKI / LIFE IN FINLAND */}
      <section className="bg-[#0E382B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-white tracking-tight">{t("home.finlandTitle")}</h2>
            <p className="mt-3 text-[#E8F0EC]/85 leading-relaxed">{t("home.finlandBody")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {HELSINKI.map((img, i) => (
              <div key={i} className="rounded-2xl overflow-hidden h-52 sm:h-64" data-testid={`helsinki-photo-${i}`}>
                <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SYLLABUS */}
      <section id="syllabus" className="bg-white border-y border-[#E2DFD7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0E382B] tracking-tight">{t("home.coveredTitle")}</h2>
            <p className="mt-3 text-[#52635B] text-base leading-relaxed">{t("home.coveredSub")}</p>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <SyllabusColumn
              badge="A1"
              badgeColor={{ bg: "#E8F0EC", text: "#0E382B", border: "#B6D4C8" }}
              title={t("home.a1Title")}
              desc={t("home.a1Desc")}
              lessons={a1}
            />
            <SyllabusColumn
              badge="A2"
              badgeColor={{ bg: "#EDF2FE", text: "#1E429F", border: "#BFD3FE" }}
              title={t("home.a2Title")}
              desc={t("home.a2Desc")}
              lessons={a2}
            />
            <SyllabusColumn
              badge="B1"
              badgeColor={{ bg: "#FDEEE5", text: "#c9591f", border: "#F6C9AD" }}
              title={t("home.b1Title")}
              desc={t("home.b1Desc")}
              lessons={b1}
            />
          </div>
        </div>
      </section>

      {/* JOB GUIDANCE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20">
        <div className="rounded-3xl border border-[#E2DFD7] bg-white overflow-hidden md:flex">
          <div className="md:w-1/3 h-56 md:h-auto">
            <img src={HELSINKI[1].src} alt="Working life in Finland" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="md:w-2/3 p-8 md:p-10">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#1E429F] bg-[#E6ECFA] rounded-full px-3 py-1">
              <Briefcase className="w-3.5 h-3.5" /> {t("home.jobTag")}
            </span>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-[#0E382B] tracking-tight">{t("home.jobTitle")}</h2>
            <p className="mt-3 text-[#52635B] leading-relaxed max-w-2xl">{t("home.jobBody")}</p>
          </div>
        </div>
      </section>

      {/* ENROL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="rounded-3xl bg-[#0E382B] px-8 py-14 text-center">
          <h3 className="font-serif text-3xl sm:text-4xl text-white font-semibold">{t("home.enrolTitle")}</h3>
          <p className="mt-3 text-[#E8F0EC]/85 max-w-md mx-auto leading-relaxed">{t("home.enrolBody")}</p>
          {!user && (
            <Button onClick={login} className="mt-7 rounded-full bg-[#E46A28] hover:bg-[#c9591f] text-white px-8 h-12 text-base" data-testid="enrol-cta-button">
              <LogIn className="w-4 h-4 mr-1.5" /> {t("home.ctaEnrol")}
            </Button>
          )}
        </div>
        <p className="mt-8 text-center text-sm text-[#B5B0A2]">{t("home.footer")}</p>
      </section>
    </div>
  );
}

function SyllabusColumn({ badge, badgeColor, title, desc, lessons }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-sm font-bold px-3 py-1 rounded-full border" style={{ backgroundColor: badgeColor.bg, color: badgeColor.text, borderColor: badgeColor.border }}>
          {badge}
        </span>
        <h3 className="font-serif text-2xl font-semibold text-[#0E382B]">{title}</h3>
      </div>
      <p className="text-[#52635B] mb-5 leading-relaxed">{desc}</p>
      <ol className="space-y-2">
        {lessons.map((l, i) => (
          <li key={l.id} data-testid={`syllabus-item-${l.id}`} className="flex items-center gap-3 rounded-xl bg-[#F9F8F5] border border-[#E2DFD7] px-4 py-3">
            <span className="w-7 h-7 shrink-0 rounded-full bg-white border border-[#E2DFD7] flex items-center justify-center text-xs font-semibold text-[#52635B]">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-[#16201B] truncate">{l.title}</p>
              <p className="text-xs text-[#52635B] flex items-center gap-1.5"><BookOpen className="w-3 h-3" /> {l.grammarFocus}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-serif text-3xl font-semibold text-white">{n}</div>
      <div className="text-sm">{label}</div>
    </div>
  );
}
